// Central version + policy content. Bump versions to force users to
// re-acknowledge changelog / privacy / terms on next login.

export const APP_VERSION = "3.5";
export const CHANGELOG_VERSION = "3.5"; // bump to re-prompt users
export const PRIVACY_VERSION = "1.2"; // bumped for HR → Finance Manager rename
export const TERMS_VERSION = "1.2"; // bumped for HR → Finance Manager rename

const ALL = ["*"];
const ADMIN = ["super_admin", "hr"];

// Each item may specify `roles`. If omitted or "*" it's visible to everyone.
// Otherwise only the listed roles will see that bullet.
export const CHANGELOG = [
  {
    version: "3.5",
    date: "Feb 2026",
    items: [
      { roles: ALL, text: "Approval chain shortened to 2 stages — Department Manager → Finance Manager. Super Admin no longer approves; instead they handle payment (Mark as Paid, singly or in bulk)." },
      { roles: ALL, text: "New 'Mark as paid on approval' checkbox on Travel & Expense submission — the claim is auto-marked Paid once the Finance Manager approves." },
      { roles: ALL, text: "Each approval stage now has a 24-hour SLA. A red 'Delayed' badge appears when a stage is overdue." },
      { roles: ["super_admin"], text: "Super Admin can select multiple approved claims and mark them Paid in bulk from the Approvals → Completed tab. Reversal comments are now optional for Super Admin." },
      { roles: ALL, text: "Prev / Next pagination added to Travel Approval and Expense Claims lists so large teams can browse filtered results page-by-page." },
    ],
  },
  {
    version: "3.4",
    date: "Aug 2026",
    items: [
      { roles: ALL, text: "The HR role has been renamed to Finance Manager everywhere in BMS. All Travel, Expense and Project-Expense approvals now flow: Department Manager → Finance Manager → Super Admin." },
      { roles: ALL, text: "Excel exports on Travel & Expense pages now include richer columns — Line Items, From/To route, Attachments count, Approval Stage, Rejection Reason and full approval trail." },
      { roles: ALL, text: "Date-range filter now matches the actual expense/travel date (not just when you filed the claim), and the last day of the range is fully inclusive." },
    ],
  },
  {
    version: "3.3",
    date: "Jul 2026",
    items: [
      { roles: ALL, text: "Travel Approval & Expense pages now have professional filters — search by claim ID / employee / description, filter by employee, department, status, expense type, approval stage and date range." },
      { roles: ALL, text: "Every claim now has a Claim ID and an auto-generated Claim Date; Expense Date and Submission Date/Time are tracked separately." },
      { roles: ALL, text: "Summary cards at the top of Travel & Expense pages show Pending, Approved, Rejected, Paid, Total Claims and Total Amount at a glance." },
      { roles: ALL, text: "New Export to Excel button — exports every record matching your current filters (not just the current page) with employee, amount, approval trail and rejection info." },
      { roles: ["super_admin", "hr", "finance_manager"], text: "When Finance files a claim it now goes directly to Super Admin (Finance no longer approves their own claim)." },
      { roles: ALL, text: "Page size selector (25 / 50 / 100) with backend pagination for large teams." },
    ],
  },
  {
    version: "3.2",
    date: "Jul 2026",
    items: [
      { roles: ALL, text: "Travel module has been renamed to Travel Approval." },
      { roles: ["super_admin", "hr", "sales_manager"], text: "New Project Expense module — file expenses tagged to an Account & Opportunity. Approval flow: Finance Manager → Sales Manager → Super Admin (or Sales Manager → Super Admin directly)." },
      { roles: ALL, text: "Post updates & comments on any task while you change its progress — visible to the task creator and all assignees." },
      { roles: ALL, text: "Opportunities now display a Create Date that auto-fills on creation. (Super Admin can override this date later if needed.)" },
      { roles: ALL, text: "Your profile picture is now shown in the top bar; upload or change it from Settings → Preferences → My Profile." },
      { roles: ALL, text: "Top bar layout fixed — Search bar stays anchored on the left; notifications, theme toggle and profile menu align to the right." },
    ],
  },
  {
    version: "3.1",
    date: "Jul 2026",
    items: [
      { roles: ADMIN, text: "Approvers can now review, approve or reject Travel & Expense claims directly from the Travel / Expenses pages — no need to open the Approvals page." },
      { roles: ["super_admin"], text: "Super Admins can reverse an already-approved (or paid) Travel / Expense to Rejected with a mandatory comment." },
      { roles: ALL, text: "Travel — cancel now moves the request to Rejected (same category); the separate Cancelled tab was removed." },
      { roles: ALL, text: "Sidebar now stays fixed on desktop while the main content scrolls." },
      { roles: ALL, text: "Changelog + Privacy Policy prompts appear on first login after every deployment; users must accept to continue." },
    ],
  },
  {
    version: "3.0",
    date: "Jul 2026",
    items: [
      { roles: ADMIN, text: "New OEM master page, Salary Slip settings with company logo & auto-release, Paid status for Travel & Expense." },
      { roles: ALL, text: "Redesigned Login screen, cleaner dark-mode readability." },
      { roles: ALL, text: "Multi-line + multi-attachment Expense claims with per-line From/To for Travel Allowance and a dedicated Phone Bill category." },
      { roles: ALL, text: "Attendance calendar view + Photo Punch toggle + Regularization limit; Super Admin & HR are view-only." },
      { roles: ALL, text: "Show Password toggle everywhere; profile pictures & attachments now open reliably." },
    ],
  },
];

export const PRIVACY_POLICY = {
  version: PRIVACY_VERSION,
  updated: "Jul 2026 (rev. 2)",
  html: `
    <p><b>Swapna Meditech Industries Pvt. Ltd.</b> ("SMIPL", "we", "our") respects your privacy. This policy explains what data we collect through this platform and how we use it.</p>
    <h4>What we collect</h4>
    <ul>
      <li>Basic profile — full name, mobile number, email, employee code, department, designation.</li>
      <li>Operational data you enter — opportunities, tasks, travel requests, expense claims, inventory movements, payroll declarations.</li>
      <li>Attendance data — timestamped check-in / check-out, GPS coordinates, live selfie (only when Photo Punch is enabled by the admin).</li>
      <li>Uploaded documents — attachments to expenses, travel, payslips, and profile pictures.</li>
    </ul>
    <h4>How we use it</h4>
    <ul>
      <li>To operate CRM, payroll, attendance, expense settlement and inventory processes.</li>
      <li>To generate reports for management and statutory filings (PF, TDS, PT etc.).</li>
      <li>To secure access via authentication logs and audit trails.</li>
    </ul>
    <h4>Who can see it</h4>
    <ul>
      <li>You always see your own records.</li>
      <li>Managers / Finance Manager see records for their team as per role. Super Admins see everything.</li>
      <li>Attendance selfies are only viewable by Finance Manager & Super Admin.</li>
    </ul>
    <h4>Storage & retention</h4>
    <ul>
      <li>Data is stored on encrypted cloud infrastructure managed by SMIPL. Files are stored in an object store with token-authenticated access.</li>
      <li>Records are retained for the duration of your engagement plus applicable statutory retention periods.</li>
    </ul>
    <h4>Your choices</h4>
    <ul>
      <li>You can request correction of personal data from Finance Manager.</li>
      <li>You can raise attendance regularization requests within the monthly limit.</li>
      <li>For any privacy concern, reach out to Finance Manager at the address in your appointment letter.</li>
    </ul>
    <p class="text-xs opacity-70 mt-3">By continuing you acknowledge that you have read and understood this privacy policy and consent to the processing of your data as described above.</p>
  `,
};

export const TERMS = {
  version: TERMS_VERSION,
  updated: "Jul 2026 (rev. 2)",
  html: `
    <p>By logging into this Swapna Meditech Industries Pvt. Ltd. ("SMIPL") internal business platform ("Platform") you agree to the following terms:</p>
    <ul>
      <li><b>Confidentiality.</b> All customer, employee, payroll, opportunity and inventory data is confidential. You will not export, share, screenshot or forward it outside SMIPL without authorization.</li>
      <li><b>Accurate entries.</b> You will enter honest, accurate and timely data — including attendance, expense claims and reimbursements.</li>
      <li><b>Approvals & payments.</b> Claims are subject to your manager, Finance Manager and Super Admin approval. Fraudulent or duplicate claims will be rejected and may result in disciplinary action.</li>
      <li><b>Account security.</b> You are responsible for keeping your password confidential. Do not share credentials.</li>
      <li><b>Attendance & GPS.</b> If enabled by admin, punching attendance captures a live selfie and GPS location for audit purposes. By continuing you consent to this capture during check-in / check-out.</li>
      <li><b>Access is at-will.</b> Access can be modified or revoked by Finance Manager / Super Admin at any time.</li>
    </ul>
    <p class="text-xs opacity-70 mt-3">These terms may be updated from time to time. When updated, you'll be asked to accept them again on your next login.</p>
  `,
};

// Filter a role-scoped item list against the current user role. Falls back to
// "everyone" when the item does not declare a roles array.
export const itemVisibleToRole = (item, role) => {
  const roles = item?.roles;
  if (!roles || roles.includes("*")) return true;
  return roles.includes(role);
};

const LS_PREFIX = "smipl_agreed_";
export const isAgreed = (userId, kind, version) =>
  localStorage.getItem(`${LS_PREFIX}${userId}_${kind}_${version}`) === "1";
export const markAgreed = (userId, kind, version) =>
  localStorage.setItem(`${LS_PREFIX}${userId}_${kind}_${version}`, "1");
